<template>
  <main class="brand-home" :style="cssVars">
    <div class="ambient ambient--one" />
    <div class="ambient ambient--two" />

    <section class="hero">
      <div class="shell hero__grid">
        <div class="hero__copy">
          <span class="eyebrow reveal">{{ site.eyebrow }}</span>
          <h1 class="reveal">{{ site.headline }}</h1>
          <p class="hero__description reveal">{{ site.description }}</p>
          <div class="hero__actions reveal">
            <a class="btn btn--primary" :href="'mailto:' + site.email">Contact {{ site.brand }}</a>
            <a class="btn btn--secondary" href="/" @click.prevent="scrollTo('solutions')">Explore Solutions</a>
          </div>
          <div class="hero__contact reveal">
            <span>Official contact</span>
            <a :href="'mailto:' + site.email">{{ site.email }}</a>
          </div>
        </div>
        <div class="hero__panel reveal">
          <div class="glass-card glass-card--large">
            <span class="panel-label">Official Positioning</span>
            <h2>{{ site.brand }}</h2>
            <p>{{ site.companyLine }}</p>
          </div>
          <div class="hero__panel-grid">
            <div class="glass-card">
              <span class="panel-label">Primary Domain</span>
              <p>{{ site.domain }}</p>
            </div>
            <div class="glass-card">
              <span class="panel-label">Audience Fit</span>
              <p>Advertisers, publishers, app teams, and media partners operating at scale.</p>
            </div>
          </div>
        </div>
      </div>

      <div class="shell metric-grid">
        <div v-for="metric in site.metrics" :key="metric.label" class="metric-card reveal">
          <strong>{{ metric.value }}</strong>
          <span>{{ metric.label }}</span>
        </div>
      </div>
    </section>

    <section id="platform" class="section">
      <div class="shell section__head">
        <span class="section-kicker reveal">Platform Capabilities</span>
        <h2 class="reveal">Every core page now reflects a more credible official website standard.</h2>
        <p class="reveal">The rebuild removes template leftovers, sharpens the business narrative, and makes the site feel like a real operating company instead of a demo theme.</p>
      </div>
      <div class="shell feature-grid">
        <article v-for="item in site.capabilities" :key="item.title" class="feature-card reveal">
          <span class="feature-card__tag">Capability</span>
          <h3>{{ item.title }}</h3>
          <p>{{ item.text }}</p>
        </article>
      </div>
    </section>

    <section id="solutions" class="section section--alt">
      <div class="shell section__head">
        <span class="section-kicker reveal">Solutions</span>
        <h2 class="reveal">Designed for both demand-side growth and supply-side monetization conversations.</h2>
      </div>
      <div class="shell solution-grid">
        <article v-for="item in site.solutions" :key="item.title" class="solution-card reveal">
          <span class="solution-card__tag">{{ item.tag }}</span>
          <h3>{{ item.title }}</h3>
          <p>{{ item.text }}</p>
          <ul>
            <li v-for="point in item.points" :key="point">{{ point }}</li>
          </ul>
        </article>
      </div>
    </section>

    <section id="workflow" class="section">
      <div class="shell workflow">
        <div class="workflow__copy">
          <span class="section-kicker reveal">Operating Model</span>
          <h2 class="reveal">How {{ site.brand }} works with partners.</h2>
          <p class="reveal">The new structure gives each brand a clearer commercial story: fit review first, execution model second, optimization third.</p>
        </div>
        <div class="workflow__steps">
          <div v-for="(step, index) in site.workflow" :key="step" class="workflow-step reveal">
            <span class="workflow-step__index">0{{ index + 1 }}</span>
            <p>{{ step }}</p>
          </div>
        </div>
      </div>
    </section>

    <section id="trust" class="section section--alt">
      <div class="shell section__head">
        <span class="section-kicker reveal">Coverage</span>
        <h2 class="reveal">Representative use cases for this brand.</h2>
      </div>
      <div class="shell trust-grid">
        <span v-for="item in site.trust" :key="item" class="trust-chip reveal">{{ item }}</span>
      </div>
    </section>

    <section id="contact" class="section section--contact">
      <div class="shell contact-shell reveal">
        <div class="contact-shell__copy">
          <span class="section-kicker">Contact</span>
          <h2>Lead with a site that looks ready for serious business conversations.</h2>
          <p>{{ site.footerText }}</p>
        </div>
        <div class="contact-card">
          <div class="contact-row">
            <span>Email</span>
            <a :href="'mailto:' + site.email">{{ site.email }}</a>
          </div>
          <div class="contact-row">
            <span>Website</span>
            <span>{{ site.domain }}</span>
          </div>
          <div class="contact-row">
            <span>Coverage</span>
            <span>{{ site.trust.slice(0, 3).join(' / ') }}</span>
          </div>
          <a class="btn btn--primary btn--block" :href="'mailto:' + site.email">Start A Conversation</a>
        </div>
      </div>
    </section>
  </main>
</template>

<script>
import site from "../../siteContent";

export default {
  data() {
    return {
      site,
      observer: null
    };
  },
  computed: {
    cssVars() {
      return {
        "--brand-primary": this.site.palette.primary,
        "--brand-secondary": this.site.palette.secondary,
        "--brand-accent": this.site.palette.accent
      };
    }
  },
  mounted() {
    this.observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            this.observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.16 }
    );

    this.$el.querySelectorAll(".reveal").forEach(node => this.observer.observe(node));
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    scrollTo(id) {
      const node = document.getElementById(id);
      if (node) {
        node.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }
};
</script>

<style scoped lang="scss">
.brand-home {
  position: relative;
  overflow: hidden;
  min-height: 100vh;
  background:
    radial-gradient(circle at top left, rgba(255, 255, 255, 0.08), transparent 26rem),
    radial-gradient(circle at 85% 15%, rgba(255, 255, 255, 0.06), transparent 22rem),
    linear-gradient(180deg, #08111f 0%, #0d1830 48%, #091120 100%);
  color: #f7fafc;
  padding-top: 5.25rem;
}

.ambient {
  position: absolute;
  border-radius: 999px;
  filter: blur(26px);
  opacity: 0.42;
  pointer-events: none;
}

.ambient--one {
  width: 22rem;
  height: 22rem;
  top: 3rem;
  right: -6rem;
  background: radial-gradient(circle, var(--brand-primary), transparent 70%);
}

.ambient--two {
  width: 18rem;
  height: 18rem;
  left: -4rem;
  top: 38rem;
  background: radial-gradient(circle, var(--brand-secondary), transparent 70%);
}

.shell {
  width: min(1180px, calc(100% - 2rem));
  margin: 0 auto;
  position: relative;
  z-index: 2;
}

.hero {
  padding: 3rem 0 2rem;
}

.hero__grid {
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(0, 0.92fr);
  gap: 2rem;
  align-items: center;
}

.eyebrow,
.section-kicker,
.feature-card__tag,
.solution-card__tag,
.panel-label {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.38rem 0.8rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  font-family: "Space Grotesk", sans-serif;
  font-size: 0.75rem;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.hero__copy h1,
.section__head h2,
.workflow__copy h2,
.contact-shell__copy h2 {
  margin: 0;
  font-family: "Space Grotesk", sans-serif;
  font-size: clamp(2.6rem, 6vw, 4.6rem);
  line-height: 1.02;
  max-width: 12ch;
}

.section__head h2,
.workflow__copy h2,
.contact-shell__copy h2 {
  font-size: clamp(2rem, 4vw, 3.1rem);
  max-width: 18ch;
}

.hero__description,
.section__head p,
.workflow__copy p,
.contact-shell__copy p {
  margin: 1.15rem 0 0;
  max-width: 44rem;
  color: rgba(234, 241, 248, 0.74);
  line-height: 1.7;
}

.hero__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.9rem;
  margin-top: 1.6rem;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 3.15rem;
  padding: 0 1.3rem;
  border-radius: 999px;
  border: 1px solid transparent;
  font-weight: 700;
  text-decoration: none;
  transition: transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease;
}

.btn:hover {
  transform: translateY(-2px);
}

.btn--primary {
  color: #fff;
  background: linear-gradient(135deg, var(--brand-primary), var(--brand-secondary));
  box-shadow: 0 18px 40px rgba(12, 22, 40, 0.36);
}

.btn--secondary {
  color: #fff;
  border-color: rgba(255, 255, 255, 0.16);
  background: rgba(255, 255, 255, 0.04);
}

.btn--block {
  width: 100%;
  margin-top: 1rem;
}

.hero__contact {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1.35rem;
  padding: 0.92rem 1rem;
  border-radius: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
}

.hero__contact span {
  color: rgba(234, 241, 248, 0.68);
}

.hero__contact a {
  color: #fff;
  text-decoration: none;
}

.hero__panel {
  padding: 1rem;
  border-radius: 30px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.03));
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 24px 80px rgba(3, 8, 20, 0.34);
}

.glass-card {
  padding: 1.35rem;
  border-radius: 24px;
  background: rgba(6, 13, 24, 0.78);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.glass-card h2 {
  margin: 0.8rem 0 0.6rem;
  font-family: "Space Grotesk", sans-serif;
  font-size: clamp(1.8rem, 4vw, 2.4rem);
}

.glass-card p {
  margin: 0;
  color: rgba(234, 241, 248, 0.74);
}

.hero__panel-grid {
  display: grid;
  gap: 1rem;
  margin-top: 1rem;
}

.metric-grid,
.feature-grid,
.solution-grid,
.trust-grid {
  display: grid;
  gap: 1rem;
}

.metric-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-top: 2rem;
}

.metric-card,
.feature-card,
.solution-card,
.workflow-step,
.contact-card {
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(9, 17, 31, 0.76);
  box-shadow: 0 24px 80px rgba(3, 8, 20, 0.34);
}

.metric-card {
  padding: 1.2rem 1.25rem;
}

.metric-card strong {
  display: block;
  color: #fff;
  font-family: "Space Grotesk", sans-serif;
  font-size: 1.32rem;
}

.metric-card span {
  display: block;
  margin-top: 0.32rem;
  color: rgba(234, 241, 248, 0.68);
}

.section {
  padding: 1.2rem 0 4.8rem;
}

.section--alt {
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.03), transparent 85%);
}

.section__head {
  margin-bottom: 1.6rem;
}

.feature-grid,
.solution-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.feature-card,
.solution-card {
  padding: 1.55rem;
}

.feature-card h3,
.solution-card h3 {
  margin: 0.85rem 0 0.75rem;
  font-size: 1.3rem;
  color: #fff;
}

.feature-card p,
.solution-card p,
.solution-card li {
  color: rgba(234, 241, 248, 0.72);
  line-height: 1.7;
}

.solution-card ul {
  margin: 1rem 0 0;
  padding-left: 1.15rem;
}

.solution-card li + li {
  margin-top: 0.45rem;
}

.workflow {
  display: grid;
  grid-template-columns: minmax(0, 0.85fr) minmax(0, 1.15fr);
  gap: 1.2rem;
  align-items: start;
}

.workflow__steps {
  display: grid;
  gap: 1rem;
}

.workflow-step {
  display: flex;
  gap: 1rem;
  padding: 1.3rem 1.35rem;
}

.workflow-step__index {
  display: grid;
  place-items: center;
  width: 3rem;
  height: 3rem;
  flex: 0 0 3rem;
  border-radius: 1rem;
  background: linear-gradient(135deg, var(--brand-primary), var(--brand-secondary));
  color: #fff;
  font-family: "Space Grotesk", sans-serif;
  font-weight: 700;
}

.workflow-step p {
  margin: 0;
  color: rgba(234, 241, 248, 0.74);
}

.trust-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.trust-chip {
  padding: 1rem 1.1rem;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  background: rgba(255, 255, 255, 0.04);
  text-align: center;
  color: #fff;
}

.section--contact {
  padding-bottom: 5.4rem;
}

.contact-shell {
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(320px, 0.9fr);
  gap: 1rem;
  padding: 1.2rem;
  border-radius: 30px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.08), rgba(255, 255, 255, 0.03));
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.contact-card {
  padding: 1.4rem;
}

.contact-row {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.9rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  color: rgba(234, 241, 248, 0.72);
}

.contact-row:last-of-type {
  border-bottom: 0;
}

.contact-row a {
  color: #fff;
  text-decoration: none;
}

.reveal {
  opacity: 0;
  transform: translateY(18px);
  transition: opacity 480ms ease, transform 480ms ease;
}

.reveal.is-visible {
  opacity: 1;
  transform: translateY(0);
}

@media (max-width: 960px) {
  .hero__grid,
  .metric-grid,
  .feature-grid,
  .solution-grid,
  .workflow,
  .trust-grid,
  .contact-shell {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 640px) {
  .shell {
    width: min(1180px, calc(100% - 1.2rem));
  }

  .hero {
    padding-top: 2.4rem;
  }
}
</style>
